// ── ObservabilityProvider ────────────────────────────────────────────────────
// React context provider that binds org/workspace context to all child
// component observability. MFEs wrap their root with this component.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { trace } from '@opentelemetry/api';
import { Logger } from '../logger/logger';
import { ConsoleTransport } from '../logger/transports/console';
import { SentryBreadcrumbTransport } from '../logger/transports/sentry-breadcrumb';
import { captureError, setSentryContext } from '../sentry/init';
import type { ObservabilityContextValue, ObservabilityProviderProps } from '../types';
import { normalizeRejectionReason } from './unhandledRejection';
const ObservabilityContext = createContext<ObservabilityContextValue | null>(null);
export function ObservabilityProvider({
  mfeName,
  orgId,
  orgSlug,
  workspaceId,
  children
}: ObservabilityProviderProps): React.JSX.Element {
  // DEBUG-ONLY "last trace_id seen" state — surfaced via useLastTraceId for dev
  // tooling. NOT the product feedback path. Ref: PRID v3 / ASC-4482.
  const [lastTraceId, setLastTraceId] = useState<string | null>(null);
  const value = useMemo<ObservabilityContextValue>(() => {
    // Create MFE-scoped logger with tenant context bound
    const logger = new Logger({
      service: mfeName,
      // SentryBreadcrumbTransport forwards structured logs as Sentry breadcrumbs
      // (FR-202 / ASC-3655). Graceful no-op when Sentry is not initialized.
      transports: [new ConsoleTransport(), new SentryBreadcrumbTransport()],
      context: {
        mfeName,
        ...(orgId && {
          orgId
        }),
        ...(orgSlug && {
          orgSlug
        }),
        ...(workspaceId && {
          workspaceId
        })
      }
    });

    // Get MFE-scoped OTEL tracer (from global provider set by shell)
    let tracer = null;
    try {
      tracer = trace.getTracer(`@platform/observezen-react/${mfeName}`);
    } catch {
      // OTEL not initialized — tracer stays null
    }

    // Enrich Sentry context with tenant info
    setSentryContext({
      orgId,
      orgSlug,
      workspaceId,
      mfeName
    });
    return {
      mfeName,
      orgId,
      orgSlug,
      workspaceId,
      logger,
      tracer,
      lastTraceId,
      setLastTraceId
    };
  }, [mfeName, orgId, orgSlug, workspaceId, lastTraceId]);

  // FR-029 (ASC-6341) — a rejected promise with no `.catch()` never throws
  // during render, so `TracedErrorBoundary` never sees it; it silently
  // vanishes without this listener. Registered once per mounted provider
  // (mfeName doesn't change across the provider's lifetime in practice, but
  // reading it via a ref avoids re-subscribing on every `value` recompute —
  // e.g. every `setLastTraceId` call — which would otherwise churn the
  // window-level listener on each debug trace-id update).
  const loggerRef = useRef(value.logger);
  loggerRef.current = value.logger;
  useEffect(() => {
    function handleUnhandledRejection(event: PromiseRejectionEvent): void {
      const error = normalizeRejectionReason(event.reason);
      loggerRef.current.error('Unhandled promise rejection', {
        message: error.message
      });
      captureError(error);
    }
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);
  return <ObservabilityContext.Provider value={value}>{children}</ObservabilityContext.Provider>;
}

/** Access the observability context from any child component. */
export function useObservability() {
  const ctx = useContext(ObservabilityContext);
  if (ctx === null) {
    throw new Error("useObservability must be used within an <ObservabilityProvider>");
  }
  return ctx;
}